<template>
  <div class="flex h-full flex-col">
    <!-- Filter Bar -->
    <div class="mb-4 flex items-center justify-between gap-4">
      <!-- Search & Date Range -->
      <div class="flex flex-1 items-center gap-4">
        <SmInput
          v-model="filter.search"
          outline
          size="medium"
          design="form"
          :label="searchUsersLabel"
          class="persons-search-input h-full max-w-xs" />
        <SmDatepicker
          v-model:daterange="filter.dateRange"
          is-range
          is-dropdown
          :min-date="null"
          :quick-options="dateRangePresets"
          :placeholder="i18n.t('browserExtension.selectDateRange')"
          dropdown-class="w-[30rem]"
          class="w-72">
          <template #trigger>
            <CalendarIcon class="mr-4 text-contrast-muted" :size="20" />
            <span
              class="flex-1 truncate"
              :class="{ 'text-contrast-muted': !hasSelectedDateRange }">
              {{ dateRangeTriggerLabel }}
            </span>
          </template>
        </SmDatepicker>
      </div>

      <!-- Deployment Filter -->
      <div class="flex items-center gap-2">
        <SmSelect
          v-model:selection="selectedDeploymentOption"
          :options="deploymentOptions"
          :placeholder="i18n.t('browserExtension.allDeployments')"
          class="w-64">
          <template #item="{ item }">
            <span
              :class="{
                'italic text-gray-500 dark:text-gray-400': item.value === 'all',
              }">
              {{ item.label }}
            </span>
          </template>
        </SmSelect>
      </div>
    </div>

    <!-- Table Section -->
    <div class="relative flex min-h-0 flex-1 flex-col overflow-hidden">
      <div v-if="loading" class="h-full">
        <SmSkeleton :count="5" />
      </div>

      <div v-else-if="error" class="flex h-full items-center justify-center">
        <EmptyState
          :icon="TriangleAlert"
          :title="i18n.t('errorLoadingData')"
          :text="error.message"
          :create-label="i18n.t('browserExtension.retry')"
          :show-docs-btn="false"
          @create="retryFetch" />
      </div>

      <div
        v-else-if="filteredData.length === 0"
        class="flex h-full items-center justify-center">
        <EmptyState
          :icon="filter.search ? Search : Users"
          :title="
            filter.search
              ? i18n.t('browserExtension.noSearchResultsTitle')
              : i18n.t('browserExtension.noPersonsTitle')
          "
          :text="
            filter.search
              ? i18n.t('browserExtension.noSearchResultsDescription')
              : i18n.t('browserExtension.noPersonsDescription')
          "
          :create-label="i18n.t('resetFilters')"
          :show-docs-btn="false"
          @create="resetFilters" />
      </div>

      <UsersTable
        v-else
        :data="filteredData"
        :loading="loading"
        @row-click="openDrawer" />
    </div>

    <!-- Person Drawer -->
    <PersonDetailDrawer
      v-model:visibility="drawerVisible"
      :installation-id="selectedInstallationId"
      :person-email="selectedPersonEmail" />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import dayjs from 'dayjs'
  import {
    Users,
    Search,
    TriangleAlert,
    Calendar as CalendarIcon,
  } from 'lucide-vue-next'
  import SmInput from '@/components/sm/SmInput.vue'
  import SmDatepicker from '@/components/sm/SmDatepicker.vue'
  import SmSkeleton from '@/components/sm/SmSkeleton.vue'
  import SmSelect from '@/components/sm/SmSelect.vue'
  import EmptyState from '@/components/shared/EmptyState.vue'
  import UsersTable from '../dashboard/tables/UsersTable.vue'
  import PersonDetailDrawer from '../drawers/PersonDetailDrawer.vue'
  import type { UsageStatsWithEmail } from '@/client'
  import { useSessionStore } from '@/stores/sessionStore'
  import {
    useDeploymentOptions,
    useInstallationsStats,
  } from '@/stores/browserextension/browserextension.store'
  import { DEFAULT_DATE_RANGE_DAYS } from '@/stores/browserextension/constants'
  import { displayDate } from '@/utils/formatter/date'

  const i18n = useI18n()
  const sessionStore = useSessionStore()

  const browserExtensionEmailEnabled = computed(function () {
    return !!sessionStore.currentCompanyInfo?.features.browser_extension_email
  })

  const searchUsersLabel = computed(function () {
    if (browserExtensionEmailEnabled.value) {
      return i18n.t('browserExtension.searchUserIdOrEmail')
    }

    return i18n.t('browserExtension.searchUserId')
  })

  // Filter state
  const filter = ref<{
    search: string
    dateRange: Date[] | undefined
  }>({
    search: '',
    dateRange: [
      dayjs().subtract(DEFAULT_DATE_RANGE_DAYS, 'days').toDate(),
      dayjs().toDate(),
    ],
  })

  // Drawer state
  const drawerVisible = ref(false)
  const selectedInstallationId = ref<string | null>(null)
  const selectedPersonEmail = ref<string | null>(null)

  // ----------------------------------------------------------------------
  // Deployment Selection - Use shared composable
  // ----------------------------------------------------------------------
  const {
    deploymentOptions,
    selectedDeploymentOption,
    selectedDeploymentId,
    unsubscribe,
  } = useDeploymentOptions()

  // Cleanup subscriptions on unmount to prevent memory leaks
  onUnmounted(() => {
    unsubscribe()
  })

  // ----------------------------------------------------------------------
  // Date Range Presets
  // ----------------------------------------------------------------------
  const dateRangePresets = computed(() => [
    {
      label: i18n.t('browserExtension.last7Days'),
      start: dayjs().subtract(7, 'days').toDate(),
      end: dayjs().toDate(),
    },
    {
      label: i18n.t('browserExtension.last30Days'),
      start: dayjs().subtract(30, 'days').toDate(),
      end: dayjs().toDate(),
    },
    {
      label: i18n.t('browserExtension.last90Days'),
      start: dayjs().subtract(90, 'days').toDate(),
      end: dayjs().toDate(),
    },
  ])

  const hasSelectedDateRange = computed(function () {
    const dateRange = filter.value.dateRange
    return !!(dateRange && dateRange[0] && dateRange[1])
  })

  function isSameDateRange(
    startA: Date | null | undefined,
    endA: Date | null | undefined,
    startB: Date | null | undefined,
    endB: Date | null | undefined
  ) {
    if (!startA || !endA || !startB || !endB) {
      return false
    }

    return (
      dayjs(startA).isSame(startB, 'day') && dayjs(endA).isSame(endB, 'day')
    )
  }

  const selectedQuickOptionLabel = computed(function () {
    const dateRange = filter.value.dateRange

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return null
    }

    for (const option of dateRangePresets.value) {
      if (
        isSameDateRange(dateRange[0], dateRange[1], option.start, option.end)
      ) {
        return option.label
      }
    }

    return null
  })

  const dateRangeTriggerLabel = computed(function () {
    const dateRange = filter.value.dateRange
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return i18n.t('browserExtension.selectDateRange')
    }

    if (selectedQuickOptionLabel.value) {
      return selectedQuickOptionLabel.value
    }

    return (
      displayDate(dateRange[0], { dateStyle: 'medium' }) +
      ' - ' +
      displayDate(dateRange[1], { dateStyle: 'medium' })
    )
  })

  // Reactive refs for store composable
  const fromDate = computed<Date | null>(
    () => (filter.value.dateRange && filter.value.dateRange[0]) || null
  )
  const tillDate = computed<Date | null>(
    () => (filter.value.dateRange && filter.value.dateRange[1]) || null
  )

  // Fetch data from API
  const {
    data: installationsStats,
    loading,
    error,
    refetch,
  } = useInstallationsStats(fromDate, tillDate, selectedDeploymentId)

  // Filter data by search
  const filteredData = computed(() => {
    if (!filter.value.search) {
      return installationsStats.value || []
    }
    const searchLower = filter.value.search.toLowerCase()
    return (
      installationsStats.value?.filter((item) => {
        const idMatches = item.id.toLowerCase().includes(searchLower)
        const emailMatches =
          browserExtensionEmailEnabled.value &&
          !!item.email &&
          item.email.toLowerCase().includes(searchLower)

        return idMatches || emailMatches
      }) || []
    )
  })

  function resetFilters() {
    filter.value.search = ''
    filter.value.dateRange = [
      dayjs().subtract(DEFAULT_DATE_RANGE_DAYS, 'days').toDate(),
      dayjs().toDate(),
    ]

    if (deploymentOptions.value.length > 0) {
      selectedDeploymentOption.value = [deploymentOptions.value[0]]
    } else {
      selectedDeploymentOption.value = undefined
    }
  }

  const openDrawer = (_rowIndex: number, row: UsageStatsWithEmail) => {
    if (!row?.id) {
      return
    }

    selectedPersonEmail.value = row.email || null
    selectedInstallationId.value = row.id
    drawerVisible.value = true
  }

  const retryFetch = () => {
    refetch()
  }
</script>

<style scoped lang="scss">
  .persons-search-input :deep(.sm-input) {
    height: 1.5rem;
  }
</style>
